import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Search,
  Clear,
} from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

interface FormFieldProps {
  label: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

interface TextFieldProps extends FormFieldProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number';
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  showPasswordToggle?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export const FormTextField = React.forwardRef<HTMLInputElement, TextFieldProps>(({
  label,
  value,
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  type = 'text',
  placeholder,
  multiline = false,
  rows = 1,
  maxLength,
  showPasswordToggle = false,
  startIcon,
  endIcon,
  sx,
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = type === 'password';

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const inputType = isPassword && showPassword ? 'text' : type;

  return (
    <TextField
      ref={ref}
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error || helperText}
      required={required}
      disabled={disabled}
      type={inputType}
      placeholder={placeholder}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      inputProps={{
        maxLength,
      }}
      InputProps={{
        startAdornment: startIcon && (
          <InputAdornment position="start">
            {startIcon}
          </InputAdornment>
        ),
        endAdornment: (isPassword && showPasswordToggle) || endIcon ? (
          <InputAdornment position="end">
            {isPassword && showPasswordToggle && (
              <IconButton
                onClick={handleTogglePassword}
                edge="end"
                disabled={disabled}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            )}
            {endIcon}
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
        ...sx,
      }}
    />
  );
});

FormTextField.displayName = 'FormTextField';

interface SelectFieldProps extends FormFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const FormSelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  options,
  placeholder,
  sx,
}) => {
  return (
    <FormControl fullWidth error={!!error} disabled={disabled} sx={sx}>
      <InputLabel required={required}>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        sx={{ borderRadius: 2 }}
      >
        {placeholder && (
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {(error || helperText) && (
        <FormHelperText>{error || helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

interface CheckboxFieldProps extends Omit<FormFieldProps, 'required'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}

export const FormCheckboxField: React.FC<CheckboxFieldProps> = ({
  label,
  checked,
  onChange,
  error,
  helperText,
  disabled = false,
  sx,
}) => {
  return (
    <Box sx={sx}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            color="primary"
          />
        }
        label={label}
      />
      {(error || helperText) && (
        <FormHelperText error={!!error}>
          {error || helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

interface RadioGroupFieldProps extends Omit<FormFieldProps, 'required'> {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  row?: boolean;
}

export const FormRadioGroupField: React.FC<RadioGroupFieldProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  disabled = false,
  options,
  row = false,
  sx,
}) => {
  return (
    <FormControl component="fieldset" error={!!error} disabled={disabled} sx={sx}>
      <FormLabel component="legend">{label}</FormLabel>
      <RadioGroup
        value={value}
        onChange={(e) => onChange(e.target.value)}
        row={row}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio color="primary" />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      {(error || helperText) && (
        <FormHelperText>{error || helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Cerca...',
  onClear,
  disabled = false,
  sx,
}) => {
  return (
    <TextField
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search color="action" />
          </InputAdornment>
        ),
        endAdornment: value && onClear && (
          <InputAdornment position="end">
            <IconButton
              onClick={onClear}
              edge="end"
              size="small"
              disabled={disabled}
            >
              <Clear />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
        ...sx,
      }}
    />
  );
};

interface FormSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  divider?: boolean;
  sx?: SxProps<Theme>;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  subtitle,
  children,
  divider = true,
  sx,
}) => {
  return (
    <Box sx={sx}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
      {divider && <Divider sx={{ mt: 3 }} />}
    </Box>
  );
};

interface FormCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

export const FormCard: React.FC<FormCardProps> = ({
  title,
  subtitle,
  children,
  actions,
  sx,
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 2,
        ...sx,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body1" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      {children}

      {actions && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {actions}
        </Box>
      )}
    </Paper>
  );
};
